import { LaneItem, LevelManager } from './level/LevelManager';
import { VehicleManager } from './level/VehicleManager';
import { Vehicle } from './Vehicle';

export const ObjectClassification = {
  Nothing: 'nothing',
  Vehicle: 'vehicle',
  Obstacle: 'obstacle',
  BoostPad: 'boostPad'
} as const;

export type ObjectClassificationKeys = keyof typeof ObjectClassification;
export type ObjectClassificationValues = typeof ObjectClassification[ObjectClassificationKeys];

export interface LaneScanResult {
  isSafe: boolean;
  avoidIfPossible: boolean;
  score: number;
  goodObjectClassification: ObjectClassificationValues;
  badObjectClassification: ObjectClassificationValues;
  distanceToBadObject: number;
  distanceToGoodObject: number;
  closestObject: ObjectClassificationValues;
  closestObjectDistance: number;
}

export class AIController {
  protected scene: Phaser.Scene;
  protected aiVehicle: Vehicle;
  protected vehicleManager: VehicleManager;
  protected levelManager: LevelManager;

  protected safeDistance: number;
  protected maxScanDistance: number;

  protected nextVehicles: Map<number, LaneItem | undefined>;
  protected nextObstacles: Map<number, LaneItem | undefined>;
  protected nextBoostPads: Map<number, LaneItem | undefined>;
  protected laneScanResults: Map<number, LaneScanResult>;

  constructor(scene: Phaser.Scene, aiVehicle: Vehicle, vehicleManager: VehicleManager, levelManager: LevelManager) {
    this.scene = scene;
    this.aiVehicle = aiVehicle;
    this.vehicleManager = vehicleManager;
    this.levelManager = levelManager;

    this.safeDistance = 250;
    this.maxScanDistance = 1000;

    this.nextVehicles = new Map();
    this.nextObstacles = new Map();
    this.nextBoostPads = new Map();
    this.laneScanResults = new Map();

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.aiVehicle.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    });
  }

  public update(time: number, delta: number) {
    if (this.aiVehicle.IsChangingLane || this.aiVehicle.HasFinishedRun) return;

    // Scan lanes for traffic, obstacles, and boostPads.
    this.collectData();

    // Take scanned data and evaluate it for our purposes.
    this.rankLanes();

    this.decideStrategy();
  }

  protected collectData() {
    let frontX = this.aiVehicle.x + this.aiVehicle.displayOriginX;
    let backX = this.aiVehicle.x - this.aiVehicle.displayOriginX;

    if (this.aiVehicle.VehicleBody) {
      frontX = this.aiVehicle.VehicleBody.bounds.max.x;
      backX = this.aiVehicle.VehicleBody.bounds.min.x;
    }

    this.nextVehicles = this.vehicleManager.findNearestVehicleToPoint(frontX, backX, this.aiVehicle);
    this.nextObstacles = this.levelManager.findNearestObstacleToPoint(frontX, backX);
    this.nextBoostPads = this.levelManager.findNearestBoostPadToPoint(frontX, backX);
  }

  protected rankLanes() {
    let frontX = this.aiVehicle.x + this.aiVehicle.displayOriginX;
    let backX = this.aiVehicle.x - this.aiVehicle.displayOriginX;

    if (this.aiVehicle.VehicleBody) {
      frontX = this.aiVehicle.VehicleBody.bounds.max.x;
      backX = this.aiVehicle.VehicleBody.bounds.min.x;
    }

    for (let i = 0; i < this.levelManager.CurrentTheme.roadDetails.laneCount; i++) {
      let vehicleLaneItem = this.nextVehicles.get(i);
      let obstacleLaneItem = this.nextObstacles.get(i);
      let boostPadLaneItem = this.nextBoostPads.get(i);

      // Find closest.
      let closestItem: LaneItem | undefined = undefined;
      let closestDistance: number = Number.MAX_SAFE_INTEGER;
      let closestBadItem: LaneItem | undefined = undefined;
      let closestBadDistance: number = Number.MAX_SAFE_INTEGER;

      if (vehicleLaneItem) {
        if (vehicleLaneItem.distance < closestDistance) {
          closestItem = vehicleLaneItem;
          closestDistance = vehicleLaneItem.distance;
        }

        if (vehicleLaneItem.distance < closestBadDistance) {
          closestBadItem = vehicleLaneItem;
          closestBadDistance = vehicleLaneItem.distance;
        }
      }

      if (obstacleLaneItem) {
        if (obstacleLaneItem.distance < closestDistance) {
          closestItem = obstacleLaneItem;
          closestDistance = obstacleLaneItem.distance;
        }

        if (obstacleLaneItem.distance < closestBadDistance) {
          closestBadItem = obstacleLaneItem;
          closestBadDistance = obstacleLaneItem.distance;
        }
      }

      if (boostPadLaneItem && boostPadLaneItem.distance < closestDistance) {
        closestItem = boostPadLaneItem;
        closestDistance = boostPadLaneItem.distance;
      }

      this.laneScanResults.set(i, {
        isSafe: closestDistance > frontX + this.safeDistance,
        avoidIfPossible: closestItem?.classification == ObjectClassification.Vehicle ?? false,
        score: 0,
        goodObjectClassification: boostPadLaneItem?.classification ?? ObjectClassification.Nothing,
        badObjectClassification: closestBadItem?.classification ?? ObjectClassification.Nothing,
        distanceToGoodObject: Math.trunc(boostPadLaneItem?.distance ?? -1),
        distanceToBadObject: closestBadDistance > this.maxScanDistance ? Math.trunc(closestBadDistance) : Math.trunc(closestBadDistance),
        closestObject: closestItem?.classification ?? ObjectClassification.Nothing,
        closestObjectDistance: Math.trunc(closestDistance)
      });
    }
  }

  protected decideStrategy() {
    let scanResult = this.laneScanResults.get(this.aiVehicle.CurrentLane);

    // We should always have a scanResult, if not bug.
    if (!scanResult) {
      console.warn(`${this.aiVehicle.CurrentSkin} is driving blind.`);
      return;
    }

    // If current lane not safe we need to change.
    if (!scanResult.isSafe) {
      // Make sure we're moving or we can't change lane.
      if (!this.aiVehicle.IsAccelerating) this.aiVehicle.accelerate();
      this.pickBestLeftOrRight(scanResult);
      return;
    }

    // this.strategySpeed();
    // return;

    if (this.aiVehicle.HasPassengers) {
      if (!this.aiVehicle.IsAccelerating) this.aiVehicle.accelerate();

      // If have passenger
      // What's in front of us.
      // How long do we have.
      // Check left and right lane.
      this.strategyImprovedSpeed();
      return;
    } else {
      // If no passenger

      // Find vehicle with passenger.
      let { closestVehicle: foundVehicle, distanceBetween: distanceBetween } = this.vehicleManager.findClosestPassengerInVehicle(this.aiVehicle);

      // If no one has passengers then just default to speed.
      if (foundVehicle == this.aiVehicle) {
        this.strategyImprovedSpeed();
        return;
      }

      let isTargetAhead = false;
      if (foundVehicle.x > this.aiVehicle.x) isTargetAhead = true;

      // if (isTargetAhead && distanceBetween > 250) {
        this.strategyImprovedSpeed();
        return;
      // } else {
      //   this.strategyMoveClose(isTargetAhead, distanceBetween, foundVehicle, foundVehicle.CurrentLane);
      //   return;
      // }

      // if (distanceBetween < 500) {
      //   // let laneDiff = foundVehicle.CurrentLane - this.aiVehicle.CurrentLane;
      //   // this.strategyMoveClose(foundVehicle.CurrentLane);
      //   this.strategyImprovedSpeed();
      //   return;
      // } else {
      //   this.strategyImprovedSpeed();
      //   return;
      // }

      // If close is in lane above or below.
      // Else best path.

      // If behind hit boosts
      // If in front slow down.
      // Find path to lane left or right.
    }
  }

  // Move into the correct lane to attack.
  protected strategyMoveClose(isTargetAhead: boolean, distanceToTarget: number, targetVehicle: Vehicle, targetLane: number) {
    let direction = targetLane - this.aiVehicle.CurrentLane;
    let requiredLaneChanges = Math.abs(direction) - 1;

    let currentLane = this.aiVehicle.CurrentLane;
    let currentLaneScan = this.laneScanResults.get(currentLane);

    let upLane = this.aiVehicle.CurrentLane - 1;
    let upResults = this.laneScanResults.get(upLane);

    let downLane = this.aiVehicle.CurrentLane + 1;
    let downResults = this.laneScanResults.get(downLane);

    // Check current lane.
    // let bestLane = this.aiVehicle.CurrentLane;
    // let currentLaneItem = this.nextVehicles.get(this.aiVehicle.CurrentLane);

    if (direction == 1 || direction == -1) {
      // Match speed?
    } else {
      // Get in correct lane.

      // Same lane
      if (direction == 0 && currentLaneScan) {
        // Pick up or down.
        this.pickBestLeftOrRight(currentLaneScan);
      } else if (direction < -1 && upResults && upResults.isSafe) {
        // Go left/up.
        this.aiVehicle.changeLaneUp();
      } else if (direction > 1 && downResults && downResults.isSafe) {
        // Go right/down.
        this.aiVehicle.changeLaneDown();
      }
    }

    if (isTargetAhead && !this.aiVehicle.IsAccelerating) {
      this.aiVehicle.accelerate();
    } else if (!isTargetAhead && this.aiVehicle.IsAccelerating) {
      this.aiVehicle.decelerate();
    }
  }

  protected strategyImprovedSpeed() {
    if (!this.aiVehicle.IsAccelerating) this.aiVehicle.accelerate();

    let currentLane = this.aiVehicle.CurrentLane;
    let currentScanResults = this.laneScanResults.get(currentLane);

    let upLane = this.aiVehicle.CurrentLane - 1;
    let upScanResults = this.laneScanResults.get(upLane);

    let downLane = this.aiVehicle.CurrentLane + 1;
    let downScanResults = this.laneScanResults.get(downLane);

    let bestLane = currentLane;
    let bestScanResults = currentScanResults;
    let bestDistance: number = currentScanResults?.distanceToBadObject ?? -1;

    if (upScanResults && upScanResults.distanceToBadObject > bestDistance) {
      bestLane = upLane;
      bestScanResults = upScanResults;
      bestDistance = upScanResults.distanceToBadObject;
    }

    if (downScanResults && downScanResults.distanceToBadObject > bestDistance) {
      bestLane = downLane;
      bestScanResults = downScanResults;
      bestDistance = downScanResults.distanceToBadObject;
    }

    if (bestLane < this.aiVehicle.CurrentLane) {
      this.aiVehicle.changeLaneUp();
      if (!this.aiVehicle.IsBlockedLeftOrUp)
        console.log(
          `${this.aiVehicle.CurrentSkin}: Moving from lane ${this.aiVehicle.CurrentLane} up to dodge ${currentScanResults?.badObjectClassification ?? '---'} ${
            currentScanResults?.distanceToBadObject
          } away into lane ${upLane} with ${bestScanResults?.badObjectClassification} ${bestScanResults?.distanceToBadObject} away`
        );
    } else if (bestLane > this.aiVehicle.CurrentLane) {
      this.aiVehicle.changeLaneDown();
      if (!this.aiVehicle.IsBlockedRightOrDown)
        console.log(
          `${this.aiVehicle.CurrentSkin}: Moving from lane ${this.aiVehicle.CurrentLane} down to dodge ${
            currentScanResults?.badObjectClassification ?? '---'
          } ${currentScanResults?.distanceToBadObject} away into lane ${upLane} with ${bestScanResults?.badObjectClassification} ${
            bestScanResults?.distanceToBadObject
          } away`
        );
    }
  }

  protected pickBestLeftOrRight(currentLaneScanResult: LaneScanResult): boolean {
    let didMakeChoice = false;

    let isUpSafe = false;
    let isDownSafe = false;

    let upLane = this.aiVehicle.CurrentLane - 1;
    let upResults = this.laneScanResults.get(upLane);

    if (upLane >= 0 && !this.aiVehicle.IsBlockedLeftOrUp) isUpSafe = upResults?.isSafe ?? false;

    let downLane = this.aiVehicle.CurrentLane + 1;
    let downResults = this.laneScanResults.get(downLane);

    if (downLane <= this.levelManager.CurrentTheme.roadDetails.laneCount - 1 && !this.aiVehicle.IsBlockedRightOrDown) isDownSafe = downResults?.isSafe ?? false;

    // If we have options, check for best option.
    if (isUpSafe && isDownSafe && upResults && downResults) {
      if (upResults.distanceToBadObject > downResults.distanceToBadObject) {
        didMakeChoice = true;
        this.aiVehicle.changeLaneUp();
        if (!this.aiVehicle.IsBlockedLeftOrUp)
          console.log(
            `${this.aiVehicle.CurrentSkin}: With two choices, moving from lane ${this.aiVehicle.CurrentLane} up to dodge ${currentLaneScanResult.badObjectClassification} ${currentLaneScanResult.distanceToBadObject} away into lane ${upLane} with ${upResults.badObjectClassification} ${upResults.distanceToBadObject} away`
          );
      } else {
        didMakeChoice = true;
        this.aiVehicle.changeLaneDown();
        if (!this.aiVehicle.IsBlockedRightOrDown)
          console.log(
            `${this.aiVehicle.CurrentSkin}: With two choices, moving from lane ${this.aiVehicle.CurrentLane} down to dodge ${currentLaneScanResult.badObjectClassification} ${currentLaneScanResult.distanceToBadObject} away into lane ${upLane} with ${upResults.badObjectClassification} ${upResults.distanceToBadObject} away`
          );
      }
    } else {
      if (isUpSafe) {
        didMakeChoice = true;
        this.aiVehicle.changeLaneUp();
        if (!this.aiVehicle.IsBlockedLeftOrUp)
          console.log(
            `${this.aiVehicle.CurrentSkin}: No choice, moving from lane ${this.aiVehicle.CurrentLane} up to dodge ${
              currentLaneScanResult.badObjectClassification
            } ${currentLaneScanResult.distanceToBadObject} away into lane ${upLane} with ${upResults?.badObjectClassification ?? '---'} ${
              upResults?.distanceToBadObject ?? '---'
            } away`
          );
      } else {
        didMakeChoice = true;
        this.aiVehicle.changeLaneDown();
        if (!this.aiVehicle.IsBlockedRightOrDown)
          console.log(
            `${this.aiVehicle.CurrentSkin}: No choice, moving from lane ${this.aiVehicle.CurrentLane} down to dodge ${
              currentLaneScanResult.badObjectClassification
            } ${currentLaneScanResult.distanceToBadObject} away into lane ${upLane} with ${upResults?.badObjectClassification ?? '---'} ${
              upResults?.distanceToBadObject ?? '---'
            } away`
          );
      }
    }

    return didMakeChoice;
  }

  protected pickBestLane(lanes: number[]) {}
}
